import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import DivergenceBadge from "@/components/DivergenceBadge";
import { AlertTriangle, Loader2, Layers, ArrowUpRight, Share2 } from "lucide-react";
import { toast } from "sonner";

export default function DisagreementPage() {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minScore, setMinScore] = useState(0.6);

  const load = useCallback(async (score = minScore) => {
    setLoading(true);
    try {
      const r = await api.get("/disagreement", { params: { min_score: score, limit: 40 } });
      setTopics(r.data);
    } catch {
      toast.error("Failed to load disagreement wall");
    } finally {
      setLoading(false);
    }
  }, [minScore]);

  useEffect(() => { load(minScore); /* eslint-disable-next-line */ }, [minScore]);

  const sharePage = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: "The Disagreement Wall — Hot Truth Archive", url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Page link copied");
      }
    } catch {/* user cancelled */}
  };

  return (
    <div className="bg-stone-900 text-stone-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <section className="border-b-2 border-[#E60000] pb-8 mb-10">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="max-w-3xl">
              <div className="font-mono-head text-xs uppercase tracking-[0.3em] text-[#E60000] mb-3 flex items-center gap-2">
                <AlertTriangle className="w-3 h-3" /> Where outlets diverge · Curated by AI
              </div>
              <h1 className="font-serif-head text-5xl sm:text-6xl leading-[0.95] mb-4">
                The <span className="italic text-[#E60000]">Disagreement</span> Wall.
              </h1>
              <p className="text-base text-stone-300 leading-relaxed max-w-2xl">
                Every topic here is being covered by multiple outlets — but framed in meaningfully
                different ways. Higher percentage = more divergent narratives. Read all sides before
                you decide.
              </p>
            </div>
            <button
              data-testid="disagreement-share-btn"
              onClick={sharePage}
              className="inline-flex items-center gap-2 border border-stone-50 font-mono-head text-xs uppercase tracking-widest px-4 py-3 hover:bg-stone-50 hover:text-stone-900 transition-colors"
            >
              <Share2 className="w-3 h-3" /> Share Wall
            </button>
          </div>
        </section>

        {/* Threshold control */}
        <div className="flex items-center flex-wrap gap-3 mb-8">
          <span className="font-mono-head text-[10px] uppercase tracking-[0.3em] text-stone-400">
            Min. divergence:
          </span>
          {[0.4, 0.55, 0.6, 0.7, 0.8].map((s) => (
            <button
              key={s}
              data-testid={`threshold-${s}`}
              onClick={() => setMinScore(s)}
              className={`font-mono-head text-xs uppercase tracking-widest px-3 py-1.5 border transition-colors ${
                minScore === s
                  ? "bg-[#E60000] text-stone-50 border-[#E60000]"
                  : "bg-transparent text-stone-300 border-stone-700 hover:border-stone-50"
              }`}
            >
              {Math.round(s * 100)}%
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 text-center font-mono-head text-xs uppercase tracking-widest text-stone-500 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Scanning disagreements…
          </div>
        ) : topics.length === 0 ? (
          <div className="py-20 text-center" data-testid="empty-disagreement">
            <AlertTriangle className="w-10 h-10 text-stone-700 mx-auto mb-4" />
            <p className="font-mono-head text-sm uppercase tracking-widest text-stone-500 mb-4">
              No topics above {Math.round(minScore * 100)}% divergence right now.
            </p>
            <p className="font-mono-head text-[10px] uppercase tracking-widest text-stone-600 max-w-md mx-auto">
              Visit individual topics and click "Analyze Divergence" to populate this wall — or
              lower the threshold above.
            </p>
            <Link
              to="/topics"
              className="inline-block mt-6 font-mono-head text-xs uppercase tracking-widest bg-[#E60000] text-stone-50 px-5 py-3 hover:bg-stone-50 hover:text-stone-900 transition-colors"
            >
              Browse all topics →
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-stone-800">
            {topics.map((t, i) => (
              <Link
                key={t.id}
                to={`/topics/${t.id}`}
                data-testid={`disagreement-card-${t.id}`}
                className="bg-stone-900 p-6 hover:bg-stone-800 transition-colors group block"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="font-serif-head text-5xl text-[#E60000] leading-none">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <DivergenceBadge divergence={t.divergence} />
                    <span className="inline-flex items-center gap-1 font-mono-head text-[10px] uppercase tracking-widest text-stone-400">
                      <Layers className="w-3 h-3" /> {t.count} outlets
                    </span>
                  </div>
                </div>
                <div className="font-mono-head text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-2">
                  {t.category}
                </div>
                <h2 className="font-serif-head text-2xl text-stone-50 leading-tight mb-3 group-hover:text-[#E60000] transition-colors">
                  {t.title}
                </h2>
                {t.divergence?.summary && (
                  <p className="text-sm leading-relaxed text-stone-300 mb-4 line-clamp-3">
                    {t.divergence.summary}
                  </p>
                )}
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="font-mono-head text-[10px] uppercase tracking-widest text-stone-500">
                    {t.sources.slice(0, 3).join(" · ")}{t.sources.length > 3 ? " · …" : ""}
                  </div>
                  <span className="inline-flex items-center gap-1 font-mono-head text-[10px] uppercase tracking-widest text-[#E60000] group-hover:translate-x-1 transition-transform">
                    All angles <ArrowUpRight className="w-3 h-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
